/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Cart dot: hide bubble when cart is empty, show when items present.
(function () {
  function updateCartDot(el) {
    var count = parseInt(el.innerText, 10);
    el.style.display = (isNaN(count) || count === 0) ? 'none' : '';
  }
  document.querySelectorAll('cart-count').forEach(function (el) {
    updateCartDot(el);
    new MutationObserver(function () { updateCartDot(el); })
      .observe(el, { childList: true, characterData: true, subtree: true });
  });
}());

// Hard fix: keep header active whenever the mouse is anywhere inside it.
// The theme re-adds header--transparent when a dropdown closes, even if the
// mouse hasn't left the header. We track mouse presence on the header element
// itself and strip the class immediately if it gets added while inside.
(function () {
  var header = document.querySelector('store-header');
  if (!header) return;

  var isMouseInside = false;

  header.addEventListener('mouseenter', function () {
    isMouseInside = true;
    header.classList.remove('header--transparent');
  });

  header.addEventListener('mouseleave', function () {
    isMouseInside = false;
  });

  new MutationObserver(function () {
    if (isMouseInside && header.classList.contains('header--transparent')) {
      header.classList.remove('header--transparent');
    }
  }).observe(header, { attributes: true, attributeFilter: ['class'] });
}());

// Homepage header: hide on scroll down, reveal black on scroll up or hover.
(function () {
  if (!document.body.classList.contains('template-index')) return;

  var section = document.getElementById('shopify-section-header');
  if (!section) return;

  var lastY = 0;
  var ticking = false;

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(function () {
        var y = window.scrollY;
        if (y <= 10) {
          section.classList.remove('header--hidden', 'header--scrolled');
        } else if (y > lastY) {
          section.classList.add('header--hidden');
          section.classList.remove('header--scrolled');
        } else {
          section.classList.remove('header--hidden');
          section.classList.add('header--scrolled');
        }
        lastY = y;
        ticking = false;
      });
      ticking = true;
    }
  }

  section.addEventListener('mouseenter', function () {
    section.classList.remove('header--hidden');
    section.classList.add('header--scrolled');
  });

  section.addEventListener('mouseleave', function () {
    if (window.scrollY > 10) {
      section.classList.add('header--hidden');
      section.classList.remove('header--scrolled');
    }
  });

  window.addEventListener('scroll', onScroll, { passive: true });
}());

// Shrink oversized titles to fit their container width instead of
// wrapping or overflowing (which would grow the page).
(function () {
  var TITLE_SELECTORS = [
    { selector: '.shopify-section--image-with-text-overlay .image-overlay__content .h2', baseSize: '200px' },
    { selector: '.dual-image__title', baseSize: '100px' }
  ];

  function fitTitles() {
    TITLE_SELECTORS.forEach(function (config) {
      document.querySelectorAll(config.selector).forEach(function (title) {
        var container = title.parentElement;
        if (!container) return;

        var baseSize = parseFloat(config.baseSize);
        title.style.fontSize = config.baseSize;
        title.style.transform = 'none';

        var containerStyle = window.getComputedStyle(container);
        var horizontalPadding = parseFloat(containerStyle.paddingLeft || 0) + parseFloat(containerStyle.paddingRight || 0);
        var available = container.clientWidth - horizontalPadding;
        var needed = title.scrollWidth;

        if (needed > available && available > 0) {
          var scaledSize = Math.floor(baseSize * (available / needed));
          title.style.fontSize = scaledSize + 'px';
        }
      });
    });
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(fitTitles, 100);
  });

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    fitTitles();
  } else {
    document.addEventListener('DOMContentLoaded', fitTitles);
  }

  window.addEventListener('load', fitTitles);
}());
